import React from "react";
import { FaFile } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdTimeline } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { queries } from "lib/build/dashboards/ltx/queries";
import { Status, Urgency } from "lib/build/forms/ltx/patientCondition";

import { Tooltip } from "src/common/components/Tooltip";
import { useFormatName } from "src/common/hooks/useFormatName";
import {
	Permission,
	useAuthState,
	useHasPermission,
	useKnownUsers
} from "src/features/auth";
import {
	useLastEventByQuery,
	useLastEventValueByQuery
} from "src/features/events";
import {
	PatientLink,
	usePatientIdParam,
	usePatientName,
	useSelectPatient
} from "src/features/patient";
import { node as patientStatementOfConsentTimm } from "src/modules/ltx/forms/patientStatementOfConsentTimm";
import { ShortStatus } from "src/modules/ltx/header/components/ShortStatus";
import { useAge } from "src/modules/ltx/header/hooks/useAge";
import { useBloodGroup } from "src/modules/ltx/header/hooks/useBloodGroup";
import { useBmi } from "src/modules/ltx/header/hooks/useBmi";
import { useCombinedTransplant } from "src/modules/ltx/header/hooks/useCombinedTransplant";
import { useHeight } from "src/modules/ltx/header/hooks/useHeight";
import { useHyperImmunised } from "src/modules/ltx/header/hooks/useHyperImmunised";
import { useLongStatus } from "src/modules/ltx/header/hooks/useLongStatus";
import { usePatientEtId } from "src/modules/ltx/header/hooks/usePatientEtId";
import { useSocialSecurityNumber } from "src/modules/ltx/header/hooks/useSocialSecurityNumber";
import { useWaitingListTime } from "src/modules/ltx/header/hooks/useWaitingListTime";
import { useWeight } from "src/modules/ltx/header/hooks/useWeight";
import { messages } from "src/modules/ltx/header/messages";
import { waitingListMessages } from "src/modules/ltx/messages";
import {
	Header as BareHeader,
	HeaderAlert,
	HeaderStatus,
	HeaderValue,
	renderTitle
} from "src/modules/shared/header";

import "./Header.css";

const urgencyToProgress = {
	[Urgency.NO]: 0,
	[Urgency.LOW]: 1,
	[Urgency.MEDIUM]: 2,
	[Urgency.HIGH]: 3,
	[Urgency.VERY_HIGH]: 4,
	[Urgency.HIGHEST]: 5
};

export const Header: React.FunctionComponent = () => {
	const canEdit = useCanEdit();
	const canViewTimeline = useCanViewTimeline();
	const status =
		useLastEventValueByQuery<Status>(queries.patientStatus) || Status.NEW;
	const longStatus = useLongStatus();
	const urgency = useLastEventValueByQuery<Urgency>(queries.urgency);
	return (
		<BareHeader
			variant={status}
			status={
				<PatientLink to="/status-history">
					<HeaderStatus
						title={longStatus}
						progress={
							urgency === undefined
								? 0
								: urgencyToProgress[urgency]
						}
					>
						<ShortStatus />
					</HeaderStatus>
				</PatientLink>
			}
			title={
				<PatientLink to="/dashboard">
					<Name />
				</PatientLink>
			}
			actions={
				<>
					<StatementOfConsentAlert />
					{canEdit && <EditLink />}
					{canViewTimeline && <TimelineLink />}
					<ReportLink />
				</>
			}
			values={
				<>
					<SocialSecurityNumber />
					<BloodGroup />
					<Age />
					<Weight />
					<Height />
					<Bmi />
					<CombinedTransplant />
					<HyperImmunised />
					<PatientEtId />
					<WaitingListTime />
				</>
			}
		/>
	);
};

function useCanEdit(): boolean {
	const patientId = usePatientIdParam();

	const { user } = useAuthState();
	const { patient } = useSelectPatient(patientId);

	const hasPatientsAllRWPerm = useHasPermission(Permission.PatientsAllRW);

	const isConnectedUser =
		user !== null &&
		patient !== null &&
		patient.connectedUsers &&
		patient.connectedUsers.includes(user.id);

	return hasPatientsAllRWPerm || isConnectedUser;
}

function useCanViewTimeline(): boolean {
	return useHasPermission(Permission.Timeline);
}

const EditLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/edit" title={formatMessage(messages.editPatient)}>
			<FiEdit />
		</PatientLink>
	);
};

const TimelineLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/timeline" title={formatMessage(messages.timeline)}>
			<MdTimeline />
		</PatientLink>
	);
};

const ReportLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/report" title={formatMessage(messages.report)}>
			<FaFile />
		</PatientLink>
	);
};

const StatementOfConsentAlert: React.FunctionComponent = () => {
	const event = useLastEventByQuery<boolean>(
		queries.signedStatementOfConsent
	);

	const { formatMessage } = useIntl();

	if (
		event !== undefined &&
		event.value === true &&
		event.attachments !== null &&
		event.attachments.length !== 0
	) {
		return null;
	}

	return (
		<PatientLink
			to={`/form/${patientStatementOfConsentTimm.id}`}
			title={formatMessage(messages.statementOfConsentAlert)}
		>
			<HeaderAlert />
		</PatientLink>
	);
};

const Name: React.FunctionComponent = () => {
	const value = usePatientName();
	return <>{value}</>;
};

const SocialSecurityNumber: React.FunctionComponent = () => {
	const value = useSocialSecurityNumber();
	return <HeaderValue variant="social-security-number">{value}</HeaderValue>;
};

const BloodGroup: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useBloodGroup();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="blood-group">{content}</HeaderValue>}
		/>
	);
};

const Age: React.FunctionComponent = () => {
	const value = useAge();
	return <HeaderValue variant="age">{value}</HeaderValue>;
};

const Weight: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useWeight();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="weight">{content}</HeaderValue>}
		/>
	);
};

const Height: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useHeight();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="height">{content}</HeaderValue>}
		/>
	);
};

const Bmi: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useBmi();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.bmi} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="bmi">{content}</HeaderValue>}
		/>
	);
};

const CombinedTransplant: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useCombinedTransplant();

	const { formatDate, formatMessage } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>{value ? formatMessage(messages.combinedTransplant) : "-"}</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="combined-transplant">
					{content}
				</HeaderValue>
			}
		/>
	);
};

const HyperImmunised: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useHyperImmunised();

	const { formatDate, formatMessage } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value ? formatMessage(messages.hyperImmunised) : "-"}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="hyper-immunised">{content}</HeaderValue>
			}
		/>
	);
};

const PatientEtId: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = usePatientEtId();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.patientEtId} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="patient-et-id">{content}</HeaderValue>
			}
		/>
	);
};

const WaitingListTime: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useWaitingListTime();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content;

	if (value !== undefined) {
		content = (
			<FormattedMessage
				{...waitingListMessages.waitingListDays}
				values={{ numberOfDays: value }}
			/>
		);
	}

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={
				<HeaderValue variant="waiting-list-time">{content}</HeaderValue>
			}
		/>
	);
};
